import { collection, getDocs, doc, getDoc, query, where } from "firebase/firestore";
import { db, LEGACY_TENANT_ID } from "@/lib/firebase";
import { logger } from "@/lib/logger";

// Define User interface
export interface User {
  id: string;
  name?: string;
  email?: string;
  role?: string;
  position?: string; // Added position field
  active?: boolean; // Added active field to track user status
  status?: 'active' | 'inactive' | 'invited'; // Added status field for invitation system
  invitedBy?: string; // User ID of who invited this user (if applicable)
  tenantId?: string;
  // Add other user fields if necessary, e.g., team, photoURL
}

// Cache für Benutzernamen, um wiederholte Firestore-Anfragen zu vermeiden
const userNameCache = new Map<string, string>();
const userCache = new Map<string, User>(); // Cache for full user objects
let cachedTenantId: string | null = null; // Track which tenant is cached

// Alle Benutzer abrufen und Cache aktualisieren
export const fetchAllUsers = async (tenantId: string = LEGACY_TENANT_ID): Promise<void> => {
  try {
    // Clear cache if we are switching tenants
    if (cachedTenantId !== tenantId) {
      userCache.clear();
      userNameCache.clear();
      cachedTenantId = tenantId;
    }

    const usersRef = collection(db, "users");

    // Always use server-side WHERE clause filtering
    // This requires all documents to have a tenantId field
    // Run the migration script (scripts/migrate-tenant-ids.ts) to add tenantId to existing documents
    const q = query(usersRef, where("tenantId", "==", tenantId));

    const usersSnapshot = await getDocs(q);

    // Clear cache before repopulating to ensure we don't mix tenants
    userCache.clear();
    userNameCache.clear();

    usersSnapshot.forEach(docSnap => {
      const data = docSnap.data() as any;

      const user: User = {
        id: docSnap.id,
        name: data.name,
        email: data.email,
        role: data.role,
        position: data.position, // Added position mapping
        active: data.active !== undefined ? data.active : true, // Default to true if not set
        status: data.status || (data.active !== false ? 'active' : 'inactive'), // Map status from active field
        invitedBy: data.invitedBy, // Who invited this user
        tenantId: data.tenantId || LEGACY_TENANT_ID,
      };
      userCache.set(docSnap.id, user);

      if (data.name) {
        userNameCache.set(docSnap.id, data.name);
      } else if (data.email) {
        userNameCache.set(docSnap.id, data.email);
      }
    });

    logger.log("Benutzernamen geladen:", userNameCache.size);
    logger.log("Benutzerobjekte geladen:", userCache.size);
  } catch (error) {
    logger.error("Fehler beim Laden aller Benutzer:", error);
  }
};

// Funktion zum Abrufen aller Benutzerobjekte (mit Cache)
export const getUsers = async (tenantId: string = LEGACY_TENANT_ID): Promise<User[]> => {
  // Always refetch if tenant changed or cache is empty
  if (userCache.size === 0 || cachedTenantId !== tenantId) {
    await fetchAllUsers(tenantId);
  }
  return Array.from(userCache.values());
};

// Funktion zum Abrufen aller Praktikanten (based on position)
export const getPraktikanten = async (tenantId: string = LEGACY_TENANT_ID): Promise<User[]> => {
  const allUsers = await getUsers(tenantId);
  // Filter by position === "praktikant" AND active status (exclude deactivated users)
  return allUsers.filter(user => user.position === "praktikant" && user.active !== false);
};

// Benutzername für eine bestimmte ID abrufen (mit Cache)
export const getUserName = async (userId: string): Promise<string> => {
  // Zuerst im Cache nachsehen
  if (userNameCache.has(userId)) {
    return userNameCache.get(userId) || "";
  }

  // Wenn nicht im Cache, aus Firestore abrufen
  try {
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const userData = userSnap.data();
      const userName = userData.name || userData.email || `Benutzer ${userId.substring(0, 5)}`;

      // Im Cache speichern
      userNameCache.set(userId, userName);
      return userName;
    }
  } catch (error) {
    console.error(`Fehler beim Abrufen des Benutzernamens für ID ${userId}:`, error);
  }

  return `Benutzer ${userId.substring(0, 5)}`;
};

// Mehrere Benutzernamen in einem Aufruf abrufen (für Performance-Optimierung)
export const getUserNames = async (userIds: string[]): Promise<Map<string, string>> => {
  const result = new Map<string, string>();
  const idsToFetch = [];

  // Zuerst im Cache nachsehen
  for (const id of userIds) {
    if (userNameCache.has(id)) {
      result.set(id, userNameCache.get(id) || "");
    } else {
      idsToFetch.push(id);
    }
  }

  // Verbleibende IDs aus Firestore abrufen
  if (idsToFetch.length > 0) {
    try {
      // Jeden Benutzer einzeln abrufen
      const fetchPromises = idsToFetch.map(async (id) => {
        const userRef = doc(db, "users", id);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const data = userSnap.data() as any;
          const userName = data.name || data.email || `Benutzer ${id.substring(0, 5)}`;

          result.set(id, userName);
          userNameCache.set(id, userName); // Im Cache speichern
        } else {
          const fallbackName = `Benutzer ${id.substring(0, 5)}`;
          result.set(id, fallbackName);
          userNameCache.set(id, fallbackName);
        }
      });

      // Alle Anfragen parallel abwarten
      await Promise.all(fetchPromises);
    } catch (error) {
      console.error("Fehler beim Abrufen mehrerer Benutzernamen:", error);

      // Fallback für alle fehlenden IDs
      idsToFetch.forEach(id => {
        if (!result.has(id)) {
          result.set(id, `Benutzer ${id.substring(0, 5)}`);
        }
      });
    }
  }

  return result;
};
